/**
 * Road Hazards Service - Real-time hazard reporting and polling
 * 
 * Features:
 * - Report hazards anonymously
 * - Poll for new hazards every 30 seconds
 * - Event-based notifications for new hazards
 * - Ghost mode for cop checkpoints
 */

export type HazardType = 'crash' | 'slowdown' | 'construction' | 'lane_closure' | 
                         'object_on_road' | 'flooded_road' | 'low_visibility' | 'cop' | 'safety_inspector';

export interface RoadHazard {
  id: string;
  type: HazardType;
  latitude: number;
  longitude: number;
  note: string | null;
  reporter_id: string | null;
  reporter_name: string;
  votes: number;
  is_ghost: boolean;
  ghost_at: string | null;
  created_at: string;
  expires_at: string;
  // Voting data
  still_there_votes?: number;
  gone_votes?: number;
  extension_applied?: boolean;
}

type HazardListener = (hazards: RoadHazard[], newHazards: RoadHazard[]) => void;

class RoadHazardsService {
  private hazards: RoadHazard[] = [];
  private listeners: Set<HazardListener> = new Set();
  private pollInterval: number | null = null;
  private apiUrl = '/api/hazards';

  // Start polling for hazards
  startPolling(intervalMs: number = 30000) {
    if (this.pollInterval) return;
    
    // Fetch immediately
    this.fetchHazards();
    
    // Then poll at interval
    this.pollInterval = window.setInterval(() => {
      this.fetchHazards();
    }, intervalMs);
    
    console.log('[Hazards] Started polling every', intervalMs / 1000, 'seconds');
  }

  // Stop polling
  stopPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
      console.log('[Hazards] Stopped polling');
    }
  }

  // Subscribe to hazard updates
  subscribe(listener: HazardListener): () => void {
    this.listeners.add(listener);
    
    // Immediately notify with current hazards
    if (this.hazards.length > 0) {
      listener(this.hazards, []);
    }
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  // Get current hazards
  getHazards(): RoadHazard[] {
    return [...this.hazards];
  }

  // Fetch hazards from API
  async fetchHazards(): Promise<RoadHazard[]> {
    try {
      const response = await fetch(this.apiUrl, { credentials: 'include' });
      const data = await response.json();
      
      if (data.success && Array.isArray(data.hazards)) {
        const oldIds = new Set(this.hazards.map(h => h.id));
        const newHazards = data.hazards.filter((h: RoadHazard) => !oldIds.has(h.id));
        
        this.hazards = data.hazards;
        
        // Always notify listeners to ensure vote count updates are reflected
        this.notifyListeners(newHazards);
        
        return this.hazards;
      }
    } catch (error) {
      console.error('[Hazards] Fetch error:', error);
    }
    
    return this.hazards;
  }

  // Report a new hazard
  async reportHazard(
    type: HazardType,
    latitude: number,
    longitude: number,
    note?: string,
    reporterId?: string,
    reporterName?: string
  ): Promise<RoadHazard | null> {
    try {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          type, 
          latitude, 
          longitude,
          note: note || null,
          reporterId: reporterId || null,
          reporterName: reporterName || 'Anonymous'
        }),
      });
      
      const data = await response.json();
      
      if (data.success && data.hazard) {
        // Add to local list immediately
        this.hazards = [data.hazard, ...this.hazards];
        this.notifyListeners([data.hazard]);
        
        console.log('[Hazards] Reported:', type, 'at', latitude, longitude);
        return data.hazard;
      } else {
        console.error('[Hazards] Report failed:', data.error);
      }
    } catch (error) {
      console.error('[Hazards] Report error:', error);
    }
    
    return null;
  }

  // Vote on a hazard (still_there or gone)
  async voteOnHazard(
    hazardId: string,
    voteType: 'still_there' | 'gone',
    userId: string,
    userLatitude?: number,
    userLongitude?: number
  ): Promise<{ success: boolean; message: string; hazardRemoved?: boolean; ghostMode?: boolean; error?: string }> {
    try {
      const response = await fetch(`${this.apiUrl}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          hazardId,
          voteType,
          userId,
          userLatitude,
          userLongitude,
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        // If hazard was removed, update local list
        if (data.hazardRemoved) {
          this.hazards = this.hazards.filter(h => h.id !== hazardId);
          this.notifyListeners([]);
        } else {
          // Update vote counts locally
          this.hazards = this.hazards.map(h => 
            h.id === hazardId 
              ? { 
                  ...h, 
                  still_there_votes: data.stillThereVotes,
                  gone_votes: data.goneVotes,
                  expires_at: data.expiresAt || h.expires_at,
                  extension_applied: data.timerExtended || h.extension_applied,
                }
              : h
          );
          this.notifyListeners([]);
        }
        
        console.log('[Hazards] Vote recorded:', voteType, 'on', hazardId);
        return { success: true, message: data.message, hazardRemoved: data.hazardRemoved, ghostMode: data.ghostMode };
      } else {
        console.warn('[Hazards] Vote failed:', data.error);
        return { success: false, message: data.error || 'Vote failed', error: data.error };
      }
    } catch (error) {
      console.error('[Hazards] Vote error:', error);
      return { success: false, message: 'Network error', error: String(error) };
    }
  }

  // Notify all listeners
  private notifyListeners(newHazards: RoadHazard[]) {
    this.listeners.forEach(listener => {
      try {
        listener(this.hazards, newHazards);
      } catch (error) {
        console.error('[Hazards] Listener error:', error);
      }
    });
  }

  // Get hazard icon info
  static getHazardInfo(type: HazardType, isGhost?: boolean): { 
    color: string; 
    emoji: string;
    label: string;
    expiresIn: string;
    durationMinutes: number;
  } {
    // Ghost cop has different display
    if (type === 'cop' && isGhost) {
      return { color: '#64748b', emoji: '👻', label: 'Checkpoint', expiresIn: '30 min', durationMinutes: 30 };
    }
    
    switch (type) {
      case 'crash':
        return { color: '#ef4444', emoji: '💥', label: 'Crash', expiresIn: '2 hours', durationMinutes: 120 };
      case 'slowdown':
        return { color: '#f59e0b', emoji: '🐢', label: 'Slowdown', expiresIn: '1 hour', durationMinutes: 60 };
      case 'construction':
        return { color: '#f97316', emoji: '🚧', label: 'Road Work', expiresIn: '24 hours', durationMinutes: 1440 };
      case 'lane_closure':
        return { color: '#8b5cf6', emoji: '🚫', label: 'Road Closed', expiresIn: '24 hours', durationMinutes: 1440 };
      case 'object_on_road':
        return { color: '#eab308', emoji: '⚠️', label: 'Debris', expiresIn: '2 hours', durationMinutes: 120 };
      case 'flooded_road':
        return { color: '#0ea5e9', emoji: '🌊', label: 'Flooded', expiresIn: '8 hours', durationMinutes: 480 };
      case 'low_visibility':
        return { color: '#64748b', emoji: '🌫️', label: 'Low Visibility', expiresIn: '1 hour', durationMinutes: 60 };
      case 'cop':
        return { color: '#3b82f6', emoji: '🚔', label: 'Cop', expiresIn: '30 min', durationMinutes: 30 };
      default:
        return { color: '#f59e0b', emoji: '⚠️', label: 'Hazard', expiresIn: '1 hour', durationMinutes: 60 };
    }
  }
}

// Singleton instance
export const roadHazardsService = new RoadHazardsService();
